import { Client } from '@hey-api/client-fetch';
import { getLogger } from '@trimble/logging';
import { putFileWithProgress } from './put-file-with-progress';
import { SplatUploadCancelledError } from './splat-upload-cancelled-error';
import { createSplatUploadPath, isSplatArchiveFile } from './splat-upload-file';
import {
  SplatDto,
  SplatUploadOptions,
  SplatUploadRequest,
} from './splats-api-service.types';

interface DatasetDto {
  readonly id: string;
}

interface DatasetPage {
  readonly items?: readonly DatasetDto[];
}

const UPLOAD_DATASET_NAME = 'Gaussian splats';

function datasetsUrl(tenantTrn: string): string {
  return `/tenants/${encodeURIComponent(tenantTrn)}/datasets`;
}

function splatUrl(tenantTrn: string, datasetId: string, splatId = ''): string {
  const suffix = splatId ? `/${splatId}` : '';
  return `${datasetsUrl(tenantTrn)}/${datasetId}/splats${suffix}`;
}

/**
 * Creates the splat, PUTs the file to its pre-signed URL, and completes the
 * upload. The returned splat can still be `uploadable` while the server
 * assembles the upload; see `waitForDownloadableSplat`.
 *
 * If the splat is created but the upload is aborted or fails before
 * completing starts, it tries to delete the splat, and does not report
 * whether that worked unless it was an abort. An abort rejects with
 * {@link SplatUploadCancelledError}. A failure to complete is not cleaned
 * up and completing cannot be aborted, because the server may already keep
 * the stored file.
 */
export async function uploadSplat(
  client: Client,
  tenantTrn: string,
  request: SplatUploadRequest,
  options: SplatUploadOptions = {}
): Promise<SplatDto> {
  const { signal, onProgress, onCreated } = options;
  let created: { datasetId: string; splatId: string } | undefined;

  try {
    signal?.throwIfAborted();
    onProgress?.({ phase: 'preparing' });
    const datasetId = await findOrCreateUploadDataset(
      client,
      tenantTrn,
      signal
    );
    const createResponse = await client.post<SplatDto>({
      url: splatUrl(tenantTrn, datasetId),
      body: {
        name: request.name,
        path: createSplatUploadPath(request.file.name),
        fileset: isSplatArchiveFile(request.file.name),
        multipart: false,
      },
      signal,
    });
    if (createResponse.error || createResponse.data?.uploadUrl == null) {
      throw new Error(
        `Failed to create Gaussian splat: ${createResponse.error ?? 'missing uploadUrl'}`
      );
    }
    created = { datasetId, splatId: createResponse.data.id };
    onCreated?.(createResponse.data);

    const total = request.file.size;
    onProgress?.({ phase: 'uploading', loaded: 0, total });
    await putFileWithProgress(createResponse.data.uploadUrl, request.file, {
      signal,
      onProgress: (loaded) =>
        onProgress?.({ phase: 'uploading', loaded, total }),
    });
    signal?.throwIfAborted();
  } catch (error) {
    const cleanedUp =
      created === undefined ||
      (await tryDeleteSplat(
        client,
        tenantTrn,
        created.datasetId,
        created.splatId
      ));
    if (signal?.aborted) {
      throw new SplatUploadCancelledError(cleanedUp);
    }
    throw error;
  }

  onProgress?.({ phase: 'completing' });
  const completed = await client.post<SplatDto>({
    url: `${splatUrl(tenantTrn, created.datasetId, created.splatId)}/complete`,
    body: {},
  });
  if (completed.error || completed.data == null) {
    throw new Error(
      `Failed to complete Gaussian splat upload: ${completed.error ?? 'empty response'}`
    );
  }
  return completed.data;
}

export async function deleteSplat(
  client: Client,
  tenantTrn: string,
  datasetId: string,
  splatId: string
): Promise<void> {
  const response = await client.delete({
    url: splatUrl(tenantTrn, datasetId, splatId),
  });
  if (response.error) {
    throw new Error(
      `Failed to delete Gaussian splat ${splatId}: ${response.error}`
    );
  }
}

async function tryDeleteSplat(
  client: Client,
  tenantTrn: string,
  datasetId: string,
  splatId: string
): Promise<boolean> {
  try {
    await deleteSplat(client, tenantTrn, datasetId, splatId);
    return true;
  } catch (error) {
    getLogger().warn(
      `Could not delete Gaussian splat ${splatId} after its upload stopped`,
      error
    );
    return false;
  }
}

/** Uploads go into the tenant's first dataset, creating one when there is none. */
async function findOrCreateUploadDataset(
  client: Client,
  tenantTrn: string,
  signal?: AbortSignal
): Promise<string> {
  const existing = await client.get<DatasetPage>({
    url: `${datasetsUrl(tenantTrn)}?PageIndex=0&PageSize=1`,
    signal,
  });
  if (existing.error) {
    throw new Error(`Splats API request failed: ${existing.error}`);
  }
  const existingId = existing.data?.items?.[0]?.id;
  if (existingId !== undefined) {
    return existingId;
  }

  const created = await client.post<DatasetDto>({
    url: datasetsUrl(tenantTrn),
    body: { name: UPLOAD_DATASET_NAME },
    signal,
  });
  if (created.error || created.data == null) {
    throw new Error(
      `Failed to create Gaussian splat dataset: ${created.error ?? 'empty response'}`
    );
  }
  return created.data.id;
}
